import { useEffect } from 'react';
import delay from 'base/utils/delay'

export interface Options {
    wrapEl?: any;
    ariaLive?:
        'polite' // 任何接收对用户重要但不会如此频繁而令人讨厌的更新的区域应该接收该属性。当用户处于空闲状态时，屏幕阅读器将朗读更改。
        | 'assertive' // 应仅用于时间敏感/关键通知，这些通知绝对需要用户立即关注。通常，对 assertive 动态区域的更改将中断屏幕阅读器当前正在进行的任何宣告。因此，它可能会非常让人讨厌并具有破坏性，应该谨慎使用。
        | 'off' // 当元素具有 aria-live="off"（或具有此隐含值的 role，例如 role="marquee" 或 role="timer"）时，仅应在焦点位于元素上或内部时才宣告元素内容的更改。
}

type IProps = {
    id: string;
    options?: Options
}
// aria 实时区域 https://developer.mozilla.org/zh-CN/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
const useAriaLive = (props: IProps) => {
    const { id, options } = props
    const { wrapEl, ariaLive = 'polite', } = options || {};

    const onSetAriaLiveContent = async (content: string, delayNum?: number) => {
        const ariaEl = document.getElementById(id)
        if (ariaEl) {
            if (delayNum) {
                await delay(delayNum)
            }
            ariaEl.innerHTML = content
        }
    }

    // 创建
    const createAriaEl = () => {
        let ariaEl = document.getElementById(id)
        if (!ariaEl) {
            ariaEl = document.createElement('div')
            ariaEl.id = id
            ariaEl.ariaLive = ariaLive
            ariaEl.style.height = '0px'
            ariaEl.style.overflow = 'hidden'

            if (wrapEl) {
                wrapEl.appendChild(ariaEl)
            } else {
                document.body.appendChild(ariaEl)
            }
        }
    }

    useEffect(()=> {
        return () => {
            const ariaEl = document.getElementById(id)
            if (ariaEl) {
                ariaEl.remove()
            }
        }
    }, [])

    useEffect(() => {
        createAriaEl()
    }, [wrapEl, id])

    return {
        onSetAriaLiveContent,
    };
};

export default useAriaLive